import { useEffect } from 'react';
import { X, Settings, Loader2 } from 'lucide-react';
import { useNotificationsStore } from '@/stores/notifications-store';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const tabs = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'read', label: 'Read' },
  { id: 'mentioned', label: 'I was mentioned' }
] as const;

export default function NotificationDrawer({ isOpen, onClose }: NotificationDrawerProps) {
  const {
    activeTab,
    items,
    isLoading,
    error,
    total,
    setActiveTab,
    load,
    loadMore,
    markAsReadLocally,
  } = useNotificationsStore();

  useEffect(() => {
    if (isOpen) {
      load(true);
    }
  }, [isOpen, activeTab, load]);

  const unreadCount = items.filter(n => n.is_read === false || n.is_read === 0).length;

  const handleTabClick = (id: 'all' | 'unread' | 'read' | 'mentioned') => {
    setActiveTab(id);
    load(true);
  };

  const handleOpen = (notification: any) => {
    if (notification.is_read === false || notification.is_read === 0) {
      markAsReadLocally(notification.id);
    }
    // if (notification.redirection_url) {
    //   window.open(notification.redirection_url, '_blank');
    // }
    console.log(notification.redirection_url);
    
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '656px',
          height: '960px',
          right: '0px',
          top: '0px'
        }}
      >
        <div className="bg-secondary-500 text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Notifications</h2>
          <button 
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`flex-1 py-3 px-4 text-base font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-black border-b-2 border-black'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.id === 'unread' && unreadCount > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-2 py-1">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(960px - 120px)' }}>
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-base font-bold text-gray-900">New for you</h3>
            <button className="text-sm text-black hover:text-blue-800 transition-colors">
              Mark all read
            </button>
          </div>

          <div className="p-4">
            {error && (
              <div className="text-sm text-red-600 mb-2">{error}</div>
            )}
            {isLoading && items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                <Loader2 className="h-6 w-6 animate-spin mb-3" />
                <span className="text-sm">Loading notifications...</span>
              </div>
            ) : items.length > 0 ? (
              <div className="space-y-3">
                {items.map((notification) => (
                  <div 
                    key={notification.id}
                    className={`p-3 rounded-lg shadow-lg  ${
                      (notification.is_read === true || notification.is_read === 1) 
                        ? 'bg-gray-50 border-gray-200' 
                        : 'bg-white'
                    }`}
                    onClick={() => handleOpen(notification)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-base text-gray-900 leading-relaxed whitespace-pre-line">
                          {notification.message}
                        </p>
                      </div>
                      <button className="ml-2 font-bold text-black hover:text-gray-600 transition-colors">
                        <Settings size={17} />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="pt-2">
                  {items.length < total && (
                    <button
                      onClick={loadMore}
                      disabled={isLoading}
                      className="w-full py-2 border rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                    >
                      {isLoading ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <div 
                  className="flex items-center justify-center"
                  style={{
                    width: '328px',
                    height: '328px'
                  }}
                >
                  <img 
                    src="/img/notification.svg" 
                    alt="Notification" 
                    className="w-full h-full"
                  />
                </div>
                <p className="text-2xl font-medium text-gray-900">No Notifications to show yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
